/**
 * Heute — operatives Cockpit (UX-Inventar T3), Dashboard-Variante.
 * UI-U3 (AC 2): rebuilt to the prototype's Dashboard composition — greeting +
 * segmented Dashboard/Brok switch, "Braucht deinen Entscheid" KPI cards, the live
 * Heute-Fälligkeiten block, Umsatz-/Bestandschart, KI-Empfehlungen InsightCards,
 * and a sticky right Aktivität rail (xl:+).
 *
 * Data honesty: the renewals block keeps its LIVE wire; every other block has no
 * endpoint yet → it renders mock data in mock mode and a design-conform EMPTY
 * state in live mode (isLiveData gate — same rule as the KundeDetail mock tabs).
 *
 * [Source: brokly-design-reference/components/brokly/heute-view.tsx + app/page.tsx]
 * [Source: docs/stories/ui-u3-core-restyle.md AC2/AC9]
 */
import { useState } from 'react';
import { ArrowRight, ListPlus, Sparkles, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { CountBadge } from '../../components/ui/CountBadge';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { HeuteRenewalsBlock } from './components/HeuteRenewalsBlock';
import { DecisionCards } from './components/DecisionCards';
import { UmsatzChart } from './components/UmsatzChart';
import { ActivityRail } from './components/ActivityRail';
import { InsightCard } from '../entity-detail/components/InsightCard';
import { BriefingView } from './components/BriefingView';
import { isLiveData } from '../../lib/data-mode';
import { dateLongDe, timeShortDe } from '../../lib/format';
import { loadBrokerSession } from '../../lib/auth/auth-session';
import { vornameFromDisplayName, tageszeitGruss } from '../brok/welcome';
import {
  HEUTE_DECISIONS,
  HEUTE_CHART_SERIES,
  HEUTE_RECOMMENDATIONS,
  HEUTE_ACTIVITY,
} from './heute-mock';

export function DashboardScreen() {
  // UI-U10: in-place Dashboard | Briefing toggle (replaces the P1b Dashboard | Brok
  // navigate-pill). Briefing renders in place; /brok is reached from inside it.
  const [view, setView] = useState<'dashboard' | 'briefing'>('dashboard');
  const live = isLiveData();

  // Endpoint-less blocks: mock arrays in mock mode, EMPTY in live mode (honesty).
  const decisions = live ? [] : HEUTE_DECISIONS;
  const chartSeries = live ? [] : HEUTE_CHART_SERIES;
  const recommendations = live ? [] : HEUTE_RECOMMENDATIONS;
  const activity = live ? [] : HEUTE_ACTIVITY;

  const vorname = vornameFromDisplayName(loadBrokerSession()?.displayName);
  const now = new Date();
  const nowIso = now.toISOString();
  const gruss = tageszeitGruss(now);

  return (
    <div className="px-6 py-5">
      <div className="mx-auto flex max-w-[1400px] gap-6">
        {/* Main column */}
        <div className="min-w-0 flex-1">
          {/* Greeting header + in-place view toggle */}
          <div className="mb-5 flex items-start justify-between gap-4">
            {view === 'dashboard' ? (
              <div>
                <h1 className="display-title">
                  {vorname ? `${gruss}, ${vorname}` : gruss}
                </h1>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="capitalize">{dateLongDe(nowIso)}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-status-dot-success" />
                    Zuletzt aktualisiert {timeShortDe(nowIso)} Uhr
                  </span>
                </p>
              </div>
            ) : (
              <div />
            )}
            <SegmentedControl
              ariaLabel="Ansicht"
              value={view}
              onValueChange={(v) => setView(v === 'briefing' ? 'briefing' : 'dashboard')}
              options={[
                { value: 'dashboard', label: 'Dashboard' },
                { value: 'briefing', label: 'Brok' },
              ]}
            />
          </div>

          {view === 'briefing' ? (
            <BriefingView />
          ) : (
            <>
          {/* Braucht deinen Entscheid */}
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              Braucht deinen Entscheid <CountBadge count={decisions.length} />
            </div>
            <Link
              to="/aufgaben"
              className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Alle anzeigen <ArrowRight className="size-3" />
            </Link>
          </div>
          <div className="mb-6">
            <DecisionCards decisions={decisions} />
          </div>

          {/* Heute Fälligkeiten — LIVE block (unchanged wire) */}
          <Card className="mb-6 p-4">
            <HeuteRenewalsBlock />
          </Card>

          {/* Umsatz / Bestand chart */}
          <div className="mb-6">
            <UmsatzChart series={chartSeries} />
          </div>

          {/* KI-Empfehlungen */}
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Sparkles className="size-4 text-primary" /> KI-Empfehlungen
          </div>
          {recommendations.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              Keine KI-Empfehlungen.
            </Card>
          ) : (
            <div className="space-y-3">
              {recommendations.map((r, i) => (
                <InsightCard
                  key={i}
                  title={r.kind}
                  tag={<Badge variant={r.tone}>{r.tag}</Badge>}
                  konfidenz={r.konfidenz}
                  actions={[
                    { action: 'task', label: 'Aufgabe erstellen', icon: ListPlus, variant: 'outline' },
                    { action: 'dismiss', label: 'Verwerfen', icon: X, variant: 'ghost', pushEnd: true },
                  ]}
                  onAction={() => {}}
                >
                  {r.text}
                </InsightCard>
              ))}
            </div>
          )}
            </>
          )}
        </div>

        {/* Right context rail (xl:+) — dashboard only; the Briefing is full-width */}
        {view === 'dashboard' && <ActivityRail items={activity} />}
      </div>
    </div>
  );
}
